import { useState, useEffect, useRef } from 'react'
import MappingHeader from './header/MappingHeader'
import ProductSelectionPanel from './product-selection-panel/ProductSelectionPanel'
import VariantMappingPanel from './variant-mapping-panel/VariantMappingPanel'
import { searchVariants } from '../../services/VariantServices'
import { getProductById, productMapCatalog } from '../../services/ProductServices'
import { searchCatalogProductsPresetV2 } from '../../services/ProductLinesServices'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import MappedVariantContainer from './mapped-variant/MappedVariantContainer'
import { createArtworksForVariant, getVariantArtworks } from '../../services/ArtworkTemplateServices'
import SelectVariantsModal from './select-variant-modal/SelectVariantsModal'
import { getCurrentStore } from '../../services/CurrentStoreServices'
import { Spinner } from 'reactstrap'
import { toaster } from '../../shared/PFToast'
import { cloneDeep } from 'lodash'

const TEN_SECONDS = 10000;
const MappingContainer = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const { productId } = useParams()
    const variantId = searchParams.get('variantId')
    const size = searchParams.get('size') || 20
    const page = searchParams.get('page') || 1

    const [originalVariants, setOriginalVariants] = useState([]) // Store unfiltered list
    const [product, setProduct] = useState(null)
    const [catalogProducts, setCatalogProducts] = useState([])
    const [selectedProduct, setSelectedProduct] = useState({})
    const [listVariants, setListVariants] = useState([])
    const [selectedVariant, setSelectedVariant] = useState(null)
    const [currentPage, setCurrentPage] = useState(page)
    const [pageSize, setPageSize] = useState(size)
    const [totalPages, setTotalPages] = useState(0)
    const [isOpen, setIsOpen] = useState(false)
    const [variantFilter, setVariantFilter] = useState({}) // options map per attribute
    const [savedVariantFilters, setSavedVariantFilters] = useState({}) // persisted selections
    const [productArtWorks, setProductArtWorks] = useState(null)
    const [variantArtworks, setVariantArtworks] = useState([])
    const [activeStep, setActiveStep] = useState(1)
    const [catalogFilters, setCatalogFilters] = useState({
        productType: 'all',
        category: '',
        search: '',
    })
    const [remapping, setRemapping] = useState(false)
    const [searching, setSearching] = useState(false)
    const searchKey = useRef('-');

    const handleBack = () => {
        const redirect = `${process.env.REACT_APP_BASE_URL || 'https://seller-staging.merchize.com'
            }/a/products/${productId}`
        window.open(redirect, '_self')
    }

    const handleProductSelect = (product) => {
        setSelectedProduct(product)
        setIsOpen(true)
    }

    const handleCloseModal = () => {
        setIsOpen(false)
        setSelectedProduct({})
    }

    const searchCatalogProducts = async (filters) => {
        const store = getCurrentStore()
        if (!store?.length) {
            toaster({
                type: 'error',
                message: 'Store not found',
            })
            return
        }
        const payload = {
            allow_search_sku: true,
            category: catalogFilters.category,
            limit: pageSize,
            page: currentPage,
            sort: { popularity: -1 },
            status: 'active',
            store_slug: getCurrentStore(),
            title: catalogFilters.search,
            allow_combine_create_product_flow: true,
            ...filters,
        }
        const newSearchKey = _generateSearchKey(payload)
        const now = Date.now()
        const [sk, timestampStr] = searchKey.current.split('-');
        const timeStamp = parseInt(timestampStr, 10) || 0;
        if ((newSearchKey === sk) && (now - timeStamp < TEN_SECONDS)) {
            return
        }
        searchKey.current = `${newSearchKey}-${now}`;
        setSearching(true)
        try {
            const resp = await searchCatalogProductsPresetV2(payload)
            setCatalogProducts(resp.data.presets)
            setTotalPages(resp.data.pages)
        } catch (error) {
            toaster({
                type: 'error',
                message: error?.message,
            })
        } finally {
            setSearching(false)
        }
    }

    const handleVariantSelect = async (variant) => {
        setSelectedVariant(variant)
        setSearchParams((prev) => {
            const newParams = new URLSearchParams(prev)
            newParams.set('variantId', variant._id)
            return newParams
        })
        if (variant.isMapped) {
            setVariantArtworks(productArtWorks?.[variant._id] || [])
            setActiveStep(4)
        } else {
            setActiveStep(1)
            const payload = {}
            if (catalogFilters.productType && catalogFilters.productType !== 'all') {
                payload.integration = catalogFilters.productType
            }
            await searchCatalogProducts(payload)
        }
    }

    const handleFilterClick = (filters) => {
        if (filters && typeof filters === 'object') {
            setSavedVariantFilters(filters)
            const filtered = originalVariants.filter((variant) => {
                return Object.entries(filters).every(([key, value]) => {
                    // Skip "All" values in filter
                    if (value === 'All') return true

                    return variant.options.some((option) => option.attribute.name === key && (option.name === value || option.slug === value))
                })
            })
            setListVariants(filtered)
            if (!filtered.find((variant) => variant._id === selectedVariant._id)) {
                setSelectedVariant(filtered[0])
            }
        }
    }

    useEffect(() => {
        searchCatalogProducts({})
        getVariantArtworks(productId).then((res) => {
            const productArtworks = res.data
            setProductArtWorks(productArtworks)
            if (variantId && !variantArtworks?.length && productArtworks?.[variantId]) {
                setVariantArtworks(productArtworks[variantId])
            }
        })
        searchVariants(productId, [], { limit: 1000 }).then((resp) => {
            if (!resp.success || !resp.data || !resp.data.map_catalog_variants.length) {
                throw new Error('Cannot fetch variants')
            }
            const variants = resp.data.map_catalog_variants
            const _filteredVariants = {}
            variants.forEach((variant) => {
                variant.isMapped = variant.map_catalog_confirmed
                variant.options.forEach((option) => {
                    if (!_filteredVariants[option.attribute.name]) {
                        _filteredVariants[option.attribute.name] = new Set()
                    }
                    _filteredVariants[option.attribute.name].add(option.name || option.slug)
                })
            })
            setOriginalVariants(variants) // Store original list
            setListVariants(variants)
            setVariantFilter(_filteredVariants)
            let selectedVariant
            if (variantId) {
                selectedVariant = variants.find((v) => v._id === variantId)
            }
            if (!selectedVariant) {
                selectedVariant = variants.find((v) => !v.isMapped) || variants[0]
            }
            setSelectedVariant(selectedVariant)
            if (!variantId) {
                setSearchParams((prev) => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set('variantId', selectedVariant?._id)
                    return newParams
                })
            }
            handleVariantSelect(selectedVariant)
        }).catch((err) => {
            toaster({
                type: 'error',
                message: err.message,
            })
        })
        getProductById(productId).then((res) => {
            setProduct(res.data)
        })
    }, [productId])

    useEffect(() => {
        if (variantId && originalVariants.length > 0 && productArtWorks) {
            const selectedVariant = originalVariants.find((v) => v._id === variantId)
            handleVariantSelect(selectedVariant)
        }
    }, [productArtWorks, originalVariants])


    useEffect(() => {
        if (!variantId) return
        const findVariant = originalVariants.find((v) => v._id === variantId)
        if (!findVariant) return
        setSelectedVariant(findVariant)
        handleVariantSelect(findVariant)
    }, [variantId])

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage)
        const payload = {
            page: newPage,
        }
        if (catalogFilters.productType && catalogFilters.productType !== 'all') {
            payload.integration = catalogFilters.productType
        }
        searchCatalogProducts(payload)
    }

    const handleFilterChange = (data) => {
        setCurrentPage(1)
        const payload = {
            page: 1,
            limit: pageSize,
            category: data.category,
            title: data.search,
        }
        if (data.productType && data.productType !== 'all') {
            payload.integration = data.productType
        }
        searchCatalogProducts(payload)
    }

    const handleRemap = async () => {
        setRemapping(true)
        if (!selectedVariant) return;
        await Promise.all([
            await productMapCatalog(productId, {
                variant_catalog_maps: [{variant_id: selectedVariant._id, sku: null}],
            }),
            await createArtworksForVariant(productId, {
                ids: [selectedVariant._id],
                sideGroups: [],
            }),
        ])
        
        await searchVariants(productId, [], { limit: 1000 }).then((resp) => {
            const variants = resp.data.map_catalog_variants;
            variants.forEach((variant) => {
                variant.isMapped = variant.map_catalog_confirmed;
            })
            setOriginalVariants(variants);
            const filtered = variants.filter((variant) => {
                return Object.entries(savedVariantFilters).every(([key, value]) => {
                    if (value === 'All') return true;
                    return variant.options.some(
                        (option) => option.attribute.name === key && (option.name === value || option.slug === value)
                    )
                });
            });
            setListVariants(filtered);
            handleVariantSelect(variants.find((v) => v._id === selectedVariant._id))
        })
        setRemapping(false)
    }

    const handleAddMoreDesign = () => {
        const catalogProductId = selectedVariant.variant_catalog.product._id
        navigate(
            `/${catalogProductId}?variantId=${selectedVariant._id}&mappingProductId=${productId}&sku=${selectedVariant.sku}`
        )
    }

    const handleRemoveArtwork = async (artwork) => {
        const newVariantArtworks = variantArtworks.filter((art) => art.side_group !== artwork.side_group)
        const res = await createArtworksForVariant(productId, {
            ids: [selectedVariant._id],
            sideGroups: newVariantArtworks
        })
        if (res.success) {
            const prodAws = cloneDeep(productArtWorks);
            prodAws[selectedVariant._id] = newVariantArtworks;
            setProductArtWorks(prodAws);
            setVariantArtworks(newVariantArtworks);
            toaster({
                type: 'success',
                message: 'Remove artwork variant successfully',
            })
        } else {
            toaster({
                type: 'error',
                message: res.message,
            })
        }
    }

    const handleApplyForChange = async (value, artwork) => {
        const newVariantArtworks = variantArtworks.map((art) => {
            if (art.side_group === artwork.side_group) {
                return { ...art, pieces_per_pack: Number(value) }
            }
            return art
        })
        const res = await createArtworksForVariant(productId, {
            ids: [selectedVariant._id],
            sideGroups: newVariantArtworks
        })
        if (res.success) {
            const prodAws = cloneDeep(productArtWorks);
            prodAws[selectedVariant._id] = newVariantArtworks;
            setProductArtWorks(prodAws);
            setVariantArtworks(newVariantArtworks);
            toaster({
                type: 'success',
                message: 'Apply for variant successfully',
            })
        } else {
            toaster({
                type: 'error',
                message: res.message,
            })
        }
    }

    const onPageSizeChange = (newPageSize) => {
        const pageSizeInt = parseInt(newPageSize, 10)
        setPageSize(pageSizeInt)
        setCurrentPage(1)
        const payload = {
            limit: pageSizeInt,
            page: 1,
        }
        if (catalogFilters.productType && catalogFilters.productType !== 'all') {
            payload.integration = catalogFilters.productType
        }
        searchCatalogProducts(payload)
    }

    const _generateSearchKey = (payload) => {
        // Generate a unique key based on the search payload, to avoid redundant searches
        const keys = [
            "category",
            "title",
            "integration",
            "limit",
            "page",
        ]

        return keys.map((key) => `${payload[key] ?? ''}`).join('|')
    }

    return (
        <div className="ShopifyMappingContainer">
            <MappingHeader onBack={handleBack} activeStep={activeStep} />

            {/* {isLoading && <Progress style={{ height: 5 }} animated value={100} />} */}

            <main className="mapping-content">
                <div className="content-title">
                    <h2>{product?.title || ''}</h2>
                </div>

                <div className="content-body">
                    <VariantMappingPanel
                        variants={listVariants}
                        selectedVariantId={selectedVariant?._id}
                        mappedCount={listVariants.filter((v) => v.map_catalog_confirmed).length}
                        totalCount={listVariants.length}
                        onVariantSelect={handleVariantSelect}
                        onFilterClick={handleFilterClick}
                        variantFilter={variantFilter}
                        selectedFilters={savedVariantFilters}
                    />
                    {selectedVariant && !selectedVariant.isMapped && (
                        <ProductSelectionPanel
                            products={catalogProducts}
                            currentPage={currentPage}
                            totalPages={totalPages}
                            filters={catalogFilters}
                            setFilters={setCatalogFilters}
                            onPageChange={handlePageChange}
                            onProductSelect={handleProductSelect}
                            onFilterChange={handleFilterChange}
                            pageSize={pageSize}
                            onPageSizeChange={onPageSizeChange}
                            searching={searching}
                        />
                    )}
                    {selectedVariant &&
                        selectedVariant.isMapped &&
                        (remapping ? (
                            <Spinner style={{color: '#007BFF'}} />
                        ) : (
                            <MappedVariantContainer
                                product={{
                                    name: selectedVariant.variant_catalog?.product?.title,
                                    sku: selectedVariant.variant_catalog?.sku,
                                    options: selectedVariant.variant_catalog?.option,
                                    image: selectedVariant.variant_catalog?.product?.thumbnail_link,
                                }}
                                artworks={variantArtworks}
                                onRemap={handleRemap}
                                onAddMoreDesign={handleAddMoreDesign}
                                onRemoveArtwork={handleRemoveArtwork}
                                onApplyForChange={handleApplyForChange}
                            />
                        ))}
                </div>
            </main>

            <SelectVariantsModal
                isOpen={selectedProduct && isOpen}
                onClose={handleCloseModal}
                catalogProduct={selectedProduct}
                product={product}
                variantId={variantId}
            />
        </div>
    )
}

export default MappingContainer



